import { useState } from "react";
import {
  FolderOpen,
  Folder,
  FileAudio,
  Music,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

const statusLabels = {
  mastered: "Mastered",
  released: "Released",
  mixing: "Mixing",
  draft: "Draft",
};

// Non-recursive flat rendering approach
const renderTreeItems = (items, depth, onSelect, selectedId, expandedIds, toggleExpand) => {
  if (!items || items.length === 0) return null;
  
  return items.map((item) => {
    const hasChildren = item.children && item.children.length > 0;
    const isProject = item.type === "project";
    const isSelected = selectedId === item.id;
    const expanded = expandedIds.has(item.id);

    const getIcon = () => {
      if (item.type === "project" || item.type === "folder") {
        return expanded ? FolderOpen : Folder;
      }
      if (item.type === "song_file") return Music;
      return FileAudio;
    };

    const Icon = getIcon();

    const handleClick = () => {
      if (hasChildren) {
        toggleExpand(item.id);
      }
      if (isProject) {
        onSelect(item.id);
      }
    };

    return (
      <div key={item.id}>
        <button
          onClick={handleClick}
          className={`w-full flex items-center gap-2 px-2 py-1.5 font-mono text-sm transition-all duration-150 ${
            isSelected
              ? "bg-meta-neon-dim text-meta-neon"
              : "text-meta-text-secondary hover:text-meta-text hover:bg-meta-surface"
          }`}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
          data-testid={`vault-item-${item.id}`}
        >
          {hasChildren && (
            <span className="w-4 h-4 flex items-center justify-center">
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </span>
          )}
          {!hasChildren && <span className="w-4" />}

          <Icon
            size={16}
            className={
              item.type === "song_file"
                ? "text-meta-neon"
                : item.type === "folder"
                ? "text-meta-warning"
                : "text-meta-text-secondary"
            }
          />

          <span className="truncate flex-1 text-left">{item.name}</span>

          {isProject && (
            <div className="flex items-center gap-2">
              {item.has_mixdown ? (
                <span className="w-2 h-2 rounded-full bg-meta-neon" title="Has Mixdown" />
              ) : (
                <span className="w-2 h-2 rounded-full bg-meta-danger" title="No Mixdown" />
              )}
              <span
                className={`text-xs px-1.5 py-0.5 rounded-sm ${
                  item.status === "released" || item.status === "mastered"
                    ? "bg-meta-neon-dim text-meta-neon"
                    : item.status === "mixing"
                    ? "bg-yellow-900/30 text-meta-warning"
                    : "bg-red-900/30 text-meta-danger"
                }`}
              >
                {statusLabels[item.status] || item.status}
              </span>
            </div>
          )}
        </button>

        {expanded && hasChildren && (
          <div>
            {renderTreeItems(item.children, depth + 1, onSelect, selectedId, expandedIds, toggleExpand)}
            {item.files &&
              item.files.map((file, idx) => (
                <div
                  key={`${item.id}-file-${idx}`}
                  className="flex items-center gap-2 px-2 py-1 font-mono text-xs text-meta-text-muted"
                  style={{ paddingLeft: `${(depth + 1) * 16 + 28}px` }}
                >
                  <FileAudio size={12} className="text-meta-info" />
                  <span className="truncate">{file}</span>
                </div>
              ))}
          </div>
        )}
      </div>
    );
  });
};

export const StudioVault = ({ tree, onSelectProject, selectedProjectId }) => {
  const [expandedIds, setExpandedIds] = useState(new Set(["proj-1", "proj-2", "proj-3", "proj-4"]));

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  if (!tree) {
    return (
      <div className="bg-meta-surface border border-meta-border p-6 h-full">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-meta-elevated rounded w-3/4" />
          <div className="h-4 bg-meta-elevated rounded w-1/2" />
          <div className="h-4 bg-meta-elevated rounded w-2/3" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-meta-surface border border-meta-border h-full flex flex-col"
      data-testid="studio-vault"
    >
      <div className="px-4 py-3 border-b border-meta-border">
        <h2 className="font-mono text-xs uppercase tracking-wider text-meta-text-muted">
          Studio Vault
        </h2>
        <p className="font-sans font-medium text-meta-text mt-1">{tree.root}</p>
      </div>

      <div className="px-4 py-2 border-b border-meta-border flex items-center gap-4 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-meta-neon" />
          <span className="text-meta-text-muted">Mixdown</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-meta-danger" />
          <span className="text-meta-text-muted">No Mixdown</span>
        </div>
      </div>

      <ScrollArea className="flex-1 p-2">
        {renderTreeItems(tree.children, 0, onSelectProject, selectedProjectId, expandedIds, toggleExpand)}
      </ScrollArea>
    </div>
  );
};
